import { toast } from '@avila-roffe/components-web';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSkillFiles } from '../../skills/skillApi';
import type { Workflow } from '../types';
import {
  createWorkflow,
  deleteWorkflow,
  getWorkflowById,
  listAttachments,
  listWorkflows,
  updateWorkflow,
} from '../workflowApi';

// ─── Query key factory ────────────────────────────────────────────────────────

export const workflowKeys = {
  all: ['workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  detail: (id: string) => [...workflowKeys.all, 'detail', id] as const,
  attachments: (id: string) => [...workflowKeys.detail(id), 'attachments'] as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export function useWorkflows(limit = 50) {
  return useQuery({
    queryKey: workflowKeys.lists(),
    queryFn: () => listWorkflows(limit),
    placeholderData: keepPreviousData,
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: () => getWorkflowById(id as string),
  });
}

// ─── Skill files ─────────────────────────────────────────────────────────────

export function useWorkflowFiles(appSlug: string | undefined, slug: string | undefined) {
  return useQuery({
    queryKey: ['workflow-files', appSlug, slug],
    enabled: !!appSlug && !!slug,
    queryFn: () => getSkillFiles(appSlug as string, slug as string),
    staleTime: 60_000,
  });
}

// ─── Attachments ──────────────────────────────────────────────────────────────

export function useWorkflowAttachments(workflowId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.attachments(workflowId ?? ''),
    enabled: !!workflowId,
    queryFn: () => listAttachments('workflow', workflowId as string),
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; slug: string; triggerMode: string }) =>
      createWorkflow(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workflowKeys.lists() });
      toast.success('Workflow created');
    },
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

export function useUpdateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; enabled?: boolean }) =>
      updateWorkflow(id, payload),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWorkflow(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workflowKeys.lists() });
      toast.success('Workflow deleted');
    },
  });
}

// Re-export Workflow type for convenience
export type { Workflow };
